import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Method } from 'core/api/fetch';
import { useUnits } from 'core/contexts/units';
import { PreviewService } from 'core/api/services/preview';
import { useExperienceDetail } from 'core/contexts/experienceDetail';
import Layout from 'components/Layout';
import { Title, CircularIndeterminate, useDebounce, TextInput, BasicTable, PaginationControlled, Chip, ExperienceDetailInfo, PreviewPriceGroup, AlertDialog } from '@utriper/core-ui';
import { Box, Button, Fade, Grid2, InputAdornment, MenuItem, TextField, ToggleButton, Typography } from '@mui/material';
import { AccessTime, Add, FilterAltOutlined, FormatListBulleted, GridOn, InfoOutlined, Search } from '@mui/icons-material';
import { ExperienceStatus, formatCurrency, timeDurationFormatter } from '@utriper/core';
import { StyledToggleButtonGroup } from './styles';
import ExperienceDetailCard from './components/ExperienceDetailCard';
import CopyButton from './components/CopyButton';
import Actions from './components/Actions';
import ExperienceDetailForm from './components/ExperienceDetailForm';

const ExperienceOptionsPage = () => {
    const { id } = useParams();
    const expIndex = parseInt(id);
    const { t: translate, i18n } = useTranslation();
    const { loading, experienceName, experienceDetails, total, getExperienceDetails } = useExperienceDetail();
    const { getUnits } = useUnits();

    const [searchFilter, setSearchFilter] = useState('');
    const searchTermDebounce = useDebounce(searchFilter, 300);
    const [statusFilter, setStatusFilter] = useState('all');
    const [orderBy, setOrderBy] = useState('ci_created_on');
    const [direction, setDirection] = useState('asc');
    const [page, setPage] = useState(1);
    const [displayMode, setDisplayMode] = useState('grid');
    const [open, setOpen] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);
    const [experienceDetailToEdit, setExperienceDetailToEdit] = useState(null);
    const MAX_PAGE = 9;

    const statusOptions = useMemo(() => {
        const statusArray = [
            ...ExperienceStatus?.getIds()?.map((id) => {
                const translated = ExperienceStatus.getTranslatedName(ExperienceStatus.status, id, i18n.language);

                return { id, name: translated?.[0] ?? '' };
            }),
        ];

        statusArray.unshift({ id: 'all', name: translate('experience_options_page.all_status') });
        return statusArray;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    useEffect(() => {
        getExperienceDetails(expIndex, {
            searchTerm: searchTermDebounce,
            statusFilter,
            pager: {
                column: orderBy,
                direction,
                page,
                pageSize: MAX_PAGE,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expIndex, page, orderBy, direction, searchTermDebounce, statusFilter]);

    useEffect(() => {
        getUnits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChangeSearch = (event) => {
        setSearchFilter(event.target.value);
        if (page !== 1) {
            setPage(1);
        }
    };

    const onChangeStatus = (event) => {
        const value = event?.target?.value;
        setStatusFilter(value);
        if (page !== 1) {
            setPage(1);
        }
    };

    const handleDisplaySort = (event, newValue) => {
        if (!newValue) return;
        setDisplayMode(newValue);
    };

    const handleSort = (column) => {
        if (orderBy === column) {
            setDirection(direction === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(column);
            setDirection('asc');
        }
    };

    const handlePage = (event, value) => {
        setPage(value);
    };

    const handleCloseForm = () => {
        setOpen(false);
        if (experienceDetailToEdit?.id) {
            setExperienceDetailToEdit(null);
        }
    };

    const handleEdit = (experienceDetail) => {
        setExperienceDetailToEdit(experienceDetail);
        setOpen(true);
    };

    const renderStatus = (status) => {
        let color = '';
        const statusTranslated = ExperienceStatus.getTranslatedName(ExperienceStatus.status, status, i18n.language);

        switch (status) {
            case ExperienceStatus.IDS.live:
                color = '#50C878';
                break;
            case ExperienceStatus.IDS.draft:
                color = '#9EC4D6';
                break;
            case ExperienceStatus.IDS.archived:
                color = '#131313bf';
                break;
            default:
                color = '#50C878';
                break;
        }

        return <Chip label={statusTranslated} sx={{ color, backgroundColor: `${color}40`, borderRadius: '4px', fontWeight: '600' }} />;
    };

    const columns = [
        {
            align: 'left',
            val: 'id',
            label: translate('experience_options_page.columns.id'),
            column: 'ci_id',
            style: { width: '320px' },
        },
        {
            align: 'left',
            val: 'description',
            label: translate('experience_options_page.columns.name'),
            column: 'ci_description_1',
            style: { width: '320px' },
        },
        // {
        //     align: 'left',
        //     val: 'codeValue',
        //     label: translate('experience_options_page.columns.code'),
        //     column: 'ci_code',
        //     style: { width: '160px' },
        // },
        {
            align: 'left',
            val: 'statusValue',
            label: translate('experience_options_page.columns.status'),
            column: 'ci_status',
            style: { width: '100px' },
        },
        {
            align: 'left',
            val: 'priceValue',
            label: translate('experience_options_page.columns.price'),
            column: 'ci_price',
            style: { width: '160px' },
        },
        {
            align: 'left',
            val: 'durationValue',
            label: translate('experience_options_page.columns.duration'),
            column: 'ci_duration',
            style: { width: '160px' },
        },
        {
            align: 'left',
            val: 'actions',
            label: ' ',
            style: { width: '100px' },
        },
    ];

    const prettifyExperienceDetails = (experienceDetails = []) =>
        experienceDetails.map((expDetail) => {
            const hasPriceGroup = Number.isInteger(expDetail?.priceGroupId) && expDetail.priceGroupId > 0;
            const hasPrice = expDetail?.currency && Number.isFinite(expDetail?.price) ? true : false;

            return {
                ...expDetail,
                id: (
                    <Grid2 sx={{ width: '160px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Grid2
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                maxWidth: 'calc(100% - 52px)',
                            }}
                        >
                            <Typography noWrap sx={{ color: '#131313bf', fontSize: '1em' }}>
                                {expDetail?.id}
                            </Typography>
                        </Grid2>
                        <CopyButton textToCopy={expDetail?.id} />
                    </Grid2>
                ),
                description: (
                    <Grid2
                        container
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="flex-start"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                            maxWidth: { xs: '160px', md: '300px' },
                        }}
                    >
                        <Typography noWrap sx={{ fontSize: '1em', fontWeight: '600', color: '#131313' }}>
                            {expDetail?.description1}
                        </Typography>
                        {expDetail?.description2 && (
                            <Typography noWrap sx={{ fontSize: '.8em', color: '#131313bf' }}>
                                {expDetail.description2}
                            </Typography>
                        )}
                    </Grid2>
                ),
                codeValue: (
                    <Grid2 sx={{ width: '160px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Grid2
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                maxWidth: 'calc(100% - 52px)',
                            }}
                        >
                            <Typography noWrap sx={{ color: '#131313bf', fontSize: '1em' }}>
                                {expDetail?.code}
                            </Typography>
                        </Grid2>
                        <CopyButton textToCopy={expDetail?.code} />
                    </Grid2>
                ),
                statusValue: renderStatus(expDetail?.status),
                priceValue: hasPriceGroup ? <PreviewPriceGroup id={expDetail.priceGroupId} label="name" previewService={PreviewService} route={`/price-group/${expDetail.priceGroupId}`} styles={{ color: '#131313bf', fontWeight: '600', fontSize: '1em' }} /> : <Typography sx={{ color: hasPrice ? '#137DC5' : '#131313bf', fontWeight: '600', fontSize: '1em' }}>{hasPrice ? formatCurrency(expDetail?.price, expDetail?.currency, i18n.language) : 'Auto'}</Typography>,
                durationValue:
                    expDetail?.duration === 0 || expDetail?.duration ? (
                        <Grid2 container width="fit-content" justifyContent="space-between" alignItems="center" gap="8px">
                            <AccessTime sx={{ color: '#131313bf' }} />
                            <Typography sx={{ color: '#131313bf', fontWeight: '600', fontSize: '1em' }}>{expDetail?.duration === 0 ? '0' : timeDurationFormatter(expDetail?.duration, translate)}</Typography>
                        </Grid2>
                    ) : (
                        ''
                    ),
                actions: <Actions experienceId={expIndex} experienceDetail={expDetail} handleEdit={handleEdit} />,
            };
        });

    return (
        <Layout>
            <Box m={3} color="#131313 !important">
                {/* Header */}
                <Grid2 container justifyContent="space-between" alignItems="center" mt={2}>
                    <Grid2 container direction="column" justifyContent="center" alignItems="flex-start">
                        <Title value={experienceName ? experienceName : loading ? translate('experience_options_page.header.loading') : translate('experience_options_page.header.not_found')} styles={{ color: '#131313', fontWeight: 900, margin: 0 }} />
                        <Grid2 container justifyContent="flex-start" alignItems="center" spacing={1} flexWrap="nowrap">
                            <Title value={translate('experience_options_page.header.options')} size="m" styles={{ color: '#131313' }} />
                            <InfoOutlined onClick={() => setOpenInfo(true)} sx={{ color: '#137DC5', cursor: 'pointer' }} />
                        </Grid2>
                    </Grid2>
                    <Button
                        onClick={() => {
                            setExperienceDetailToEdit(null);
                            setOpen(true);
                        }}
                        variant="contained"
                        sx={{ backgroundColor: '#137DC5', color: 'white' }}
                        startIcon={<Add />}
                    >
                        {translate('experience_options_page.header.add')}
                    </Button>
                </Grid2>

                {/* Filters */}
                <Grid2 container my={2} spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
                    <Grid2 size={8}>
                        <TextInput
                            input={{
                                slotProps: {
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            height: '56px',
                                        },
                                    },
                                },
                                sx: {
                                    order: { xs: 2, md: 1 },
                                },
                            }}
                            meta={{ error: null, touched: null }}
                            onChange={onChangeSearch}
                            value={searchFilter}
                            label={translate('experience_options_page.filters.search')}
                            placeholder={`${translate('experience_options_page.filters.search')}...`}
                        />
                    </Grid2>
                    <Grid2 size={4} container justifyContent="space-between" alignItems="center" flexWrap="nowrap" gap="1em">
                        <TextField
                            select
                            fullWidth
                            label={translate('experience_options_page.filters.status')}
                            value={statusFilter}
                            onChange={onChangeStatus}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FilterAltOutlined sx={{ color: '#131313bf' }} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: '56px' },
                                },
                            }}
                        >
                            {statusOptions?.map((status) => (
                                <MenuItem key={status?.id} value={status?.id}>
                                    {status?.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <StyledToggleButtonGroup value={displayMode} exclusive onChange={handleDisplaySort}>
                            <ToggleButton value="grid">
                                <GridOn />
                            </ToggleButton>
                            <ToggleButton value="table">
                                <FormatListBulleted />
                            </ToggleButton>
                        </StyledToggleButtonGroup>
                    </Grid2>
                </Grid2>

                {loading ? (
                    <CircularIndeterminate centered />
                ) : (
                    <>
                        {/* Display Grid */}
                        {displayMode === 'grid' ? (
                            <Grid2 container my={2} spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
                                {experienceDetails?.map((expDetail) => (
                                    <ExperienceDetailCard experienceId={expIndex} experienceDetail={expDetail} handleEdit={handleEdit} />
                                ))}
                            </Grid2>
                        ) : (
                            // Display Table
                            <Fade in={true} timeout={400}>
                                <Grid2 container my={2} direction="column" width="100%">
                                    <BasicTable size="small" columns={columns} handleSort={handleSort} orderBy={orderBy} direction={direction} values={prettifyExperienceDetails(experienceDetails)} />
                                </Grid2>
                            </Fade>
                        )}

                        {/* Pagination */}
                        <Grid2 container justifyContent="center" alignItems="center" width="100%">
                            {total / MAX_PAGE > 1 && <PaginationControlled count={Math.ceil(total / MAX_PAGE)} page={page} onChange={handlePage} totalItems={total} itemsPerPage={MAX_PAGE} />}
                        </Grid2>
                    </>
                )}
            </Box>

            {/* Dialogs */}
            <AlertDialog open={open} dialogContent={<ExperienceDetailForm experienceId={expIndex} experienceDetail={experienceDetailToEdit} method={experienceDetailToEdit?.id ? Method.PATCH : Method.POST} onClose={handleCloseForm} />} handleClose={handleCloseForm} hideTitle hideActions maxWidth={'md'} fullWidth />

            <AlertDialog open={openInfo} dialogContent={<ExperienceDetailInfo onClose={() => setOpenInfo(false)} />} handleClose={() => setOpenInfo(false)} hideTitle hideActions maxWidth="lg" fullWidth />
        </Layout>
    );
};

export default ExperienceOptionsPage;
